import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProShell } from "@/components/ProShell";
import { toast } from "sonner";
import { Copy, Share2, Building2, Check } from "lucide-react";
import { BankSheet } from "@/pages/Referral";
import { ReferralTracker } from "@/components/ReferralTracker";

export default function ProReferral() {
    const { user } = useAuth();
    const pro = user?.professional;
    const code = pro?.referral_code || "";
    const link = `https://homiie.in/book?ref=${code}`;
    const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=102A43&data=${encodeURIComponent(link)}`;

    const [bank, setBank] = useState(undefined);
    const [showBank, setShowBank] = useState(false);
    const loadBank = () => api.get("/referral/bank").then((r) => setBank(r.data.bank)).catch(() => setBank(null));
    useEffect(() => { loadBank(); }, []);

    const copy = () => { navigator.clipboard?.writeText(code); toast.success("Referral code copied!"); };
    const shareMsg = `Book me through HOMIIE! Use my code ${code} at ${link}`;

    return (
        <ProShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-6 text-white">
                <h1 className="font-heading text-2xl font-extrabold">Referral Performance</h1>
                <p className="text-sm text-white/60">Grow your bookings with HOMIIE</p>
            </div>

            <div className="px-5 py-5">
                <div className="rounded-3xl border border-navy/10 bg-white p-6 text-center">
                    <img src={qr} alt="Referral QR" className="mx-auto h-44 w-44 rounded-xl" data-testid="referral-qr" />
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-navy/40">Your referral code</p>
                    <p data-testid="referral-code" className="font-heading text-3xl font-extrabold tracking-wider text-orange">{code}</p>
                    <p className="mt-1 text-xs text-navy/40">Pro ID: {pro?.pro_id}</p>
                    <div className="mt-4 flex gap-2">
                        <button data-testid="copy-ref-btn" onClick={copy} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-navy/15 py-3 text-sm font-bold text-navy transition-colors hover:border-orange"><Copy size={16} /> Copy</button>
                        <a data-testid="share-ref-btn" href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-orange py-3 text-sm font-bold text-white"><Share2 size={16} /> Share</a>
                    </div>
                    <p className="mt-3 text-xs text-navy/45">"Book me through HOMIIE" — rewards are based on completed qualifying transactions.</p>
                </div>

                <button data-testid="pro-referral-bank-btn" onClick={() => setShowBank(true)} className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left">
                    <Building2 size={20} className="text-teal" />
                    <div className="flex-1"><p className="font-heading font-bold text-navy">Reward payout bank</p><p className="text-xs text-navy/50">{bank ? `${bank.account_name} · ${bank.account_number_masked} · ${bank.ifsc}` : "Add a bank account in your name to receive referral rewards"}</p></div>
                    {bank ? <Check size={18} className="text-emerald-500" /> : <span className="rounded-full bg-saffron/15 px-2 py-1 text-[11px] font-bold text-saffron">Add</span>}
                </button>
                {showBank && <BankSheet current={bank} onClose={() => setShowBank(false)} onSaved={() => { setShowBank(false); loadBank(); }} />}

                <ReferralTracker />
            </div>
        </ProShell>
    );
}
